import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#initTabs();

    this.#fetchResourceGroups();
    this.#fetchRelatedSearches();
    this.#fetchRecentlyViewed();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Initializes tab bar state from URL and attaches click handlers.
   */
  #initTabs() {
    const { tabs } = this.refs;
    if (!tabs) return;

    const params = new URLSearchParams(window.location.search);
    const activeType = params.get('resource_type') || 'all';

    this.#setActiveType(activeType);

    tabs.addEventListener('click', (event) => {
      const tab = event.target.closest('.search-results__tab');
      if (!tab || tab.disabled) return;

      this.#setActiveType(tab.dataset.type);
    });
  }

  /**
   * Fetches non-product search results via Section Rendering API,
   * injects them, and builds tabs dynamically from the content.
   */
  async #fetchResourceGroups() {
    const { tabs } = this.refs;
    const resourceGroups = this.querySelector('[ref="resourceGroups"]');
    if (!tabs || !resourceGroups) return;

    const url = new URL(window.location.href);
    url.searchParams.set('type', 'article,collection');
    url.searchParams.delete('page');

    const sectionId = this.getAttribute('section-id');
    const html = await sectionRenderer.getSectionHTML(
      `shopify-section-${sectionId}`,
      false,
      url
    );

    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Extract the fetched resource groups and replace ours
    const fetchedGroups = doc.querySelector('[ref="resourceGroups"]');
    if (fetchedGroups) {
      resourceGroups.innerHTML = fetchedGroups.innerHTML;
    }

    // Build tabs dynamically from the injected content groups
    const groups = resourceGroups.querySelectorAll('.search-results__group[data-type]');
    let nonProductCount = 0;

    for (const group of groups) {
      const type = group.dataset.type;
      // Count items: blog groups use .search-results__resource-item, pages use .search-results__page-row
      const itemCount =
        group.querySelectorAll('.search-results__resource-item').length ||
        group.querySelectorAll('.search-results__page-row').length;

      nonProductCount += itemCount;

      // Extract label from the group heading (strip the count suffix)
      const heading = group.querySelector('.search-results__group-title');
      const label = heading ? heading.textContent.replace(/\s*\(\d+\)/, '').trim() : type;

      const tab = document.createElement('button');
      tab.className = 'search-results__tab button button-secondary';
      tab.dataset.type = type;
      tab.setAttribute('aria-current', 'false');
      tab.textContent = `${label} (${itemCount})`;
      if (itemCount === 0) tab.disabled = true;
      tabs.appendChild(tab);
    }

    // Don't show counts on All/Products tabs — Shopify's mixed pagination
    // makes accurate per-type totals impossible without extra requests.
    // Non-product tab counts (above) are accurate since we count rendered items.

    // Apply initial active state from URL
    const params = new URLSearchParams(window.location.search);
    const activeType = params.get('resource_type') || 'all';
    this.#setActiveType(activeType);
  }

  /**
   * Fetches related search suggestions from the Predictive Search API
   * and renders them as clickable pills.
   */
  async #fetchRelatedSearches() {
    const container = this.querySelector('[ref="relatedSearches"]');
    if (!container) return;

    const searchTerms = new URLSearchParams(window.location.search).get('q');
    if (!searchTerms) return;

    try {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', searchTerms);
      url.searchParams.set('resources[type]', 'query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'predictive-search');

      const response = await fetch(url);
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const queryItems = doc.querySelectorAll('.predictive-search-results__card--query a');

      if (queryItems.length === 0) return;

      const normalizedTerms = searchTerms.toLowerCase().trim();
      const title = container.dataset.title || 'Related Searches';
      let pillsHtml = '';

      for (const item of queryItems) {
        const text = item.textContent.trim();
        if (text.toLowerCase() === normalizedTerms) continue;
        const href = item.getAttribute('href');
        pillsHtml += `<a href="${href}" class="search-results__pill-link button button-secondary">${text}</a>`;
      }

      if (!pillsHtml) return;

      container.innerHTML = `<h3 class="search-results__group-title">${title}</h3><div class="search-results__pills-container">${pillsHtml}</div>`;
      container.hidden = false;
    } catch {
      // Silently fail — related searches are not critical
    }
  }

  /**
   * Fetches recently viewed products from localStorage and renders them
   * as product cards using the Section Rendering API.
   */
  async #fetchRecentlyViewed() {
    const container = this.querySelector('[ref="recentlyViewed"]');
    if (!container) return;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return;

    try {
      const sectionId = this.getAttribute('section-id');
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');

      const html = await sectionRenderer.getSectionHTML(
        `shopify-section-${sectionId}`,
        false,
        url
      );

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const productCards = doc.querySelectorAll('.product-grid__item');

      if (productCards.length === 0) return;

      const title = container.dataset.title || 'Recently Viewed';
      let gridHtml = `<h3 class="search-results__group-title">${title}</h3><ul class="search-results__recently-viewed-grid list-unstyled">`;

      for (const card of productCards) {
        gridHtml += card.outerHTML;
      }

      gridHtml += '</ul>';
      container.innerHTML = gridHtml;
      container.hidden = false;
    } catch {
      // Silently fail — recently viewed is not critical
    }
  }

  /**
   * Sets the active resource type tab and toggles group visibility.
   *
   * @param {string} type
   */
  #setActiveType(type) {
    const { tabs } = this.refs;
    if (!tabs) return;
    const resourceGroups = this.querySelector('[ref="resourceGroups"]');

    this.setAttribute('data-active-type', type);

    // Update tab active states
    for (const tab of tabs.querySelectorAll('.search-results__tab')) {
      tab.setAttribute('aria-current', tab.dataset.type === type ? 'true' : 'false');
    }

    // Toggle product grid visibility
    const productGrid = this.querySelector('#ResultsList');
    if (productGrid) {
      productGrid.style.display = (type !== 'all' && type !== 'product') ? 'none' : '';
    }

    // Toggle resource group visibility
    if (resourceGroups) {
      for (const group of resourceGroups.querySelectorAll('.search-results__group[data-type]')) {
        group.style.display = (type !== 'all' && group.dataset.type !== type) ? 'none' : '';
      }
    }

    // Sync to URL
    const url = new URL(window.location.href);
    if (type === 'all') {
      url.searchParams.delete('resource_type');
    } else {
      url.searchParams.set('resource_type', type);
    }
    history.replaceState(null, '', url.toString());
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
